import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Chip,
  Card,
  CardContent,
  Alert,
  Divider,
  Stack,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  FormControlLabel,
  Switch,
  Checkbox,
  LinearProgress,
  Tooltip,
  Avatar,
  Badge,
  Autocomplete,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  AccessTime as AccessTimeIcon,
  Description as DescriptionIcon,
  Category as CategoryIcon,
  Article as TemplateIcon,
  VerifiedUser as VerifiedIcon,
  Block as BlockIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  CloudUpload as CloudUploadIcon,
  Assignment as AssignmentIcon,
  CalendarToday as CalendarIcon,
  Rule as RequirementIcon,
  Assessment as ReportIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { format, differenceInDays } from 'date-fns';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const GuestDocumentsPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const { user } = useAuth();
  const token = localStorage.getItem('token');
  const [loading, setLoading] = useState(false);
  
  // State management
  const [guests, setGuests] = useState<any[]>([]);
  const [selectedGuest, setSelectedGuest] = useState<any>(null);
  const [documents, setDocuments] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [requirements, setRequirements] = useState<any[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);
  const [locations, setLocations] = useState<any[]>([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  
  // Dialog states
  const [uploadDialog, setUploadDialog] = useState(false);
  const [categoryDialog, setCategoryDialog] = useState(false);
  const [requirementDialog, setRequirementDialog] = useState(false);
  const [templateDialog, setTemplateDialog] = useState(false);
  const [documentViewDialog, setDocumentViewDialog] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<any>(null);
  
  // Form states
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadData, setUploadData] = useState({
    categoryId: '',
    documentName: '',
    documentNumber: '',
    issueDate: null,
    expiryDate: null,
    notes: ''
  });
  
  const [newCategory, setNewCategory] = useState({
    name: '',
    description: '',
    documentType: '',
    requiresExpiry: false,
    requiresVerification: false
  });
  
  const [newRequirement, setNewRequirement] = useState({
    locationId: '',
    categoryId: '',
    requirementType: '',
    isMandatory: true,
    blockCheckIn: false,
    reminderDays: 30,
    message: '',
    conditions: {
      siteTypes: [],
      hasPets: undefined,
      stayDuration: null
    }
  });

  // Filter states
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    await Promise.all([
      fetchGuests(),
      fetchCategories(),
      fetchLocations()
    ]);
  };

  const fetchGuests = async () => {
    try {
      const response = await axios.get('/api/guests', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGuests(response.data);
    } catch (error) {
      console.error('Error fetching guests:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get('/api/guest-documents/categories', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchDocuments = async (guestId: string) => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/guest-documents/guest/${guestId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRequirements = async (locationId: string) => {
    try {
      const response = await axios.get(`/api/guest-documents/requirements/location/${locationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequirements(response.data);
    } catch (error) {
      console.error('Error fetching requirements:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get('/api/guest-documents/templates', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get('/api/locations', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLocations(response.data);
      if (response.data.length > 0) {
        setSelectedLocation(response.data[0]._id);
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    
    if (newValue === 2 && selectedLocation) {
      fetchRequirements(selectedLocation);
    }
    if (newValue === 3) {
      fetchTemplates();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setUploadFile(event.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile || !selectedGuest) return;

    const formData = new FormData();
    formData.append('document', uploadFile);
    formData.append('guestId', selectedGuest._id);
    Object.keys(uploadData).forEach(key => {
      if (uploadData[key as keyof typeof uploadData] !== null) {
        formData.append(key, uploadData[key as keyof typeof uploadData] as string);
      }
    });

    try {
      await axios.post('/api/guest-documents/upload', formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setUploadDialog(false);
      setUploadFile(null);
      setUploadData({
        categoryId: '',
        documentName: '',
        documentNumber: '',
        issueDate: null,
        expiryDate: null,
        notes: ''
      });
      
      fetchDocuments(selectedGuest._id);
    } catch (error) {
      console.error('Error uploading document:', error);
    }
  };

  const handleVerifyDocument = async (documentId: string, notes?: string) => {
    try {
      await axios.post(`/api/guest-documents/${documentId}/verify`, 
        { notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (selectedGuest) {
        fetchDocuments(selectedGuest._id);
      }
    } catch (error) {
      console.error('Error verifying document:', error);
    }
  };

  const handleRejectDocument = async (documentId: string, reason: string) => {
    try {
      await axios.post(`/api/guest-documents/${documentId}/reject`, 
        { reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (selectedGuest) {
        fetchDocuments(selectedGuest._id);
      }
    } catch (error) {
      console.error('Error rejecting document:', error);
    }
  };

  const handleDownloadDocument = async (document: any) => {
    try {
      const response = await axios.get(`/api/guest-documents/${document._id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', document.fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading document:', error);
    }
  };

  const handleDeleteDocument = async (documentId: string) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      await axios.delete(`/api/guest-documents/${documentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (selectedGuest) {
        fetchDocuments(selectedGuest._id);
      }
    } catch (error) {
      console.error('Error deleting document:', error);
    }
  };

  const createCategory = async () => {
    try {
      await axios.post('/api/guest-documents/categories', newCategory, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCategoryDialog(false);
      setNewCategory({
        name: '',
        description: '',
        documentType: '',
        requiresExpiry: false,
        requiresVerification: false
      });
      
      fetchCategories();
    } catch (error) {
      console.error('Error creating category:', error);
    }
  };

  const createRequirement = async () => {
    try {
      await axios.post('/api/guest-documents/requirements', newRequirement, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setRequirementDialog(false);
      fetchRequirements(selectedLocation);
    } catch (error) {
      console.error('Error creating requirement:', error);
    }
  };

  const initializeCategories = async () => {
    try {
      await axios.post('/api/guest-documents/categories/initialize', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      fetchCategories();
    } catch (error) {
      console.error('Error initializing categories:', error);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'verified':
        return <CheckCircleIcon color="success" />;
      case 'rejected':
        return <CancelIcon color="error" />;
      case 'expired':
        return <WarningIcon color="warning" />;
      default:
        return <AccessTimeIcon color="action" />;
    }
  };

  const getStatusChip = (status: string) => {
    const statusConfig = {
      pending: { color: 'default' as const, label: 'Pending' },
      verified: { color: 'success' as const, label: 'Verified' },
      rejected: { color: 'error' as const, label: 'Rejected' },
      expired: { color: 'warning' as const, label: 'Expired' }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const filteredDocuments = documents.filter(doc => {
    const matchesStatus = statusFilter === 'all' || doc.status === statusFilter;
    const matchesCategory = categoryFilter === 'all' || doc.categoryId._id === categoryFilter;
    const matchesSearch = searchTerm === '' || 
      doc.documentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.documentNumber?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesStatus && matchesCategory && matchesSearch;
  });

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4">Guest Documents Management</Typography>
          {categories.length === 0 && (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={initializeCategories}
            >
              Initialize Default Categories
            </Button>
          )}
        </Box>

        <Paper sx={{ mb: 3 }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab icon={<DescriptionIcon />} label="Documents" />
            <Tab icon={<CategoryIcon />} label="Categories" />
            <Tab icon={<RequirementIcon />} label="Requirements" />
            <Tab icon={<TemplateIcon />} label="Templates" />
            <Tab icon={<ReportIcon />} label="Reports" />
          </Tabs>
        </Paper>

        {/* Documents Tab */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>Select Guest</Typography>
                <Autocomplete
                  options={guests}
                  getOptionLabel={(option) => `${option.name} (${option.email})`}
                  value={selectedGuest}
                  onChange={(e, value) => {
                    setSelectedGuest(value);
                    if (value) {
                      fetchDocuments(value._id);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Search Guest" variant="outlined" />
                  )}
                />
                
                {selectedGuest && (
                  <Box sx={{ mt: 2 }}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2">Guest Information</Typography>
                    <Typography variant="body2">Name: {selectedGuest.name}</Typography>
                    <Typography variant="body2">Email: {selectedGuest.email}</Typography>
                    <Typography variant="body2">Phone: {selectedGuest.phone || 'N/A'}</Typography>
                    
                    <Button
                      variant="contained"
                      startIcon={<UploadIcon />}
                      onClick={() => setUploadDialog(true)}
                      sx={{ mt: 2 }}
                      fullWidth
                    >
                      Upload Document
                    </Button>
                  </Box>
                )}
              </Paper>
            </Grid>

            <Grid item xs={12} md={8}>
              {selectedGuest ? (
                <Paper sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6">Documents</Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        size="small"
                        placeholder="Search documents..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                          )
                        }}
                      />
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Status</InputLabel>
                        <Select
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                          label="Status"
                        >
                          <MenuItem value="all">All</MenuItem>
                          <MenuItem value="pending">Pending</MenuItem>
                          <MenuItem value="verified">Verified</MenuItem>
                          <MenuItem value="rejected">Rejected</MenuItem>
                          <MenuItem value="expired">Expired</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Category</InputLabel>
                        <Select
                          value={categoryFilter}
                          onChange={(e) => setCategoryFilter(e.target.value)}
                          label="Category"
                        >
                          <MenuItem value="all">All</MenuItem>
                          {categories.map((cat) => (
                            <MenuItem key={cat._id} value={cat._id}>
                              {cat.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>

                  {loading ? (
                    <LinearProgress />
                  ) : filteredDocuments.length > 0 ? (
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Document</TableCell>
                            <TableCell>Category</TableCell>
                            <TableCell>Number</TableCell>
                            <TableCell>Expiry</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredDocuments.map((doc) => (
                            <TableRow key={doc._id}>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {getStatusIcon(doc.status)}
                                  <Typography variant="body2">{doc.documentName}</Typography>
                                </Box>
                              </TableCell>
                              <TableCell>{doc.categoryId.name}</TableCell>
                              <TableCell>{doc.documentNumber || '-'}</TableCell>
                              <TableCell>
                                {doc.expiryDate ? (
                                  <Box>
                                    <Typography variant="body2">
                                      {format(new Date(doc.expiryDate), 'MM/dd/yyyy')}
                                    </Typography>
                                    {doc.daysUntilExpiry !== null && (
                                      <Typography 
                                        variant="caption" 
                                        color={doc.daysUntilExpiry < 30 ? 'error' : 'text.secondary'}
                                      >
                                        {doc.daysUntilExpiry > 0 
                                          ? `${doc.daysUntilExpiry} days left`
                                          : `${Math.abs(doc.daysUntilExpiry)} days expired`
                                        }
                                      </Typography>
                                    )}
                                  </Box>
                                ) : '-'}
                              </TableCell>
                              <TableCell>{getStatusChip(doc.status)}</TableCell>
                              <TableCell>
                                <Tooltip title="View">
                                  <IconButton 
                                    size="small"
                                    onClick={() => {
                                      setSelectedDocument(doc);
                                      setDocumentViewDialog(true);
                                    }}
                                  >
                                    <VisibilityIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Download">
                                  <IconButton 
                                    size="small"
                                    onClick={() => handleDownloadDocument(doc)}
                                  >
                                    <DownloadIcon />
                                  </IconButton>
                                </Tooltip>
                                {doc.status === 'pending' && (
                                  <>
                                    <Tooltip title="Verify">
                                      <IconButton 
                                        size="small" 
                                        color="success"
                                        onClick={() => handleVerifyDocument(doc._id)}
                                      >
                                        <CheckCircleIcon />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Reject">
                                      <IconButton 
                                        size="small" 
                                        color="error"
                                        onClick={() => {
                                          const reason = prompt('Rejection reason:');
                                          if (reason) {
                                            handleRejectDocument(doc._id, reason);
                                          }
                                        }}
                                      >
                                        <CancelIcon />
                                      </IconButton>
                                    </Tooltip>
                                  </>
                                )}
                                <Tooltip title="Delete">
                                  <IconButton 
                                    size="small" 
                                    color="error"
                                    onClick={() => handleDeleteDocument(doc._id)}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Alert severity="info">No documents found for this guest.</Alert>
                  )}
                </Paper>
              ) : (
                <Alert severity="info">Please select a guest to view their documents.</Alert>
              )}
            </Grid>
          </Grid>
        </TabPanel>

        {/* Categories Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">Document Categories</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCategoryDialog(true)}
            >
              Add Category
            </Button>
          </Box>

          <Grid container spacing={2}>
            {categories.map((category) => (
              <Grid item xs={12} md={4} key={category._id}>
                <Card>
                  <CardContent>
                    <Typography variant="h6">{category.name}</Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {category.description || 'No description'}
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Chip 
                        label={category.documentType} 
                        size="small" 
                        color="primary" 
                        variant="outlined" 
                      />
                      {category.requiresExpiry && (
                        <Chip 
                          icon={<CalendarIcon />} 
                          label="Requires Expiry" 
                          size="small" 
                          color="warning" 
                        />
                      )}
                      {category.requiresVerification && (
                        <Chip 
                          icon={<VerifiedIcon />} 
                          label="Requires Verification" 
                          size="small" 
                          color="success" 
                        />
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </TabPanel>

        {/* Requirements Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <Typography variant="h6">Document Requirements</Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Location</InputLabel>
                  <Select
                    value={selectedLocation}
                    onChange={(e) => {
                      setSelectedLocation(e.target.value);
                      fetchRequirements(e.target.value);
                    }}
                  >
                    {locations.map((location) => (
                      <MenuItem key={location._id} value={location._id}>
                        {location.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setRequirementDialog(true)}
                  fullWidth
                >
                  Add Requirement
                </Button>
              </Grid>
            </Grid>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Document Category</TableCell>
                  <TableCell>Requirement Type</TableCell>
                  <TableCell>Mandatory</TableCell>
                  <TableCell>Blocks Check-in</TableCell>
                  <TableCell>Reminder Days</TableCell>
                  <TableCell>Conditions</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requirements.map((req) => (
                  <TableRow key={req._id}>
                    <TableCell>{req.categoryId.name}</TableCell>
                    <TableCell>
                      <Chip label={req.requirementType} size="small" />
                    </TableCell>
                    <TableCell>
                      <Checkbox checked={req.isMandatory} disabled />
                    </TableCell>
                    <TableCell>
                      <Checkbox checked={req.blockCheckIn} disabled />
                    </TableCell>
                    <TableCell>{req.reminderDays}</TableCell>
                    <TableCell>
                      {req.conditions.siteTypes?.length > 0 && (
                        <Typography variant="caption" display="block">
                          Sites: {req.conditions.siteTypes.join(', ')}
                        </Typography>
                      )}
                      {req.conditions.hasPets !== undefined && (
                        <Typography variant="caption" display="block">
                          Pets: {req.conditions.hasPets ? 'Yes' : 'No'}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <IconButton size="small">
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" color="error">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Templates Tab */}
        <TabPanel value={tabValue} index={3}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">Document Templates</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setTemplateDialog(true)}
            >
              Add Template
            </Button>
          </Box>

          <Grid container spacing={2}>
            {templates.map((template) => (
              <Grid item xs={12} md={6} key={template._id}>
                <Card>
                  <CardContent>
                    <Typography variant="h6">{template.name}</Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {template.description}
                    </Typography>
                    <Typography variant="caption">
                      Category: {template.categoryId?.name || 'N/A'}
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" display="block">
                        Fields: {template.fields?.length || 0}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </TabPanel>

        {/* Reports Tab */}
        <TabPanel value={tabValue} index={4}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Expiring Documents Report
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    View all documents expiring within the specified time frame
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    <TextField
                      type="number"
                      label="Days Ahead"
                      defaultValue={30}
                      size="small"
                    />
                    <Button variant="outlined" startIcon={<ReportIcon />}>
                      Generate Report
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Compliance Report
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Check guest compliance with document requirements
                  </Typography>
                  <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <InputLabel>Location</InputLabel>
                    <Select value={selectedLocation} label="Location">
                      {locations.map((location) => (
                        <MenuItem key={location._id} value={location._id}>
                          {location.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button variant="outlined" startIcon={<AssignmentIcon />}>
                    Generate Report
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Upload Dialog */}
        <Dialog open={uploadDialog} onClose={() => setUploadDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Upload Document</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <FormControl fullWidth>
                <InputLabel>Document Category</InputLabel>
                <Select
                  value={uploadData.categoryId}
                  onChange={(e) => setUploadData({ ...uploadData, categoryId: e.target.value })}
                  label="Document Category"
                >
                  {categories.map((cat) => (
                    <MenuItem key={cat._id} value={cat._id}>
                      {cat.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Document Name"
                value={uploadData.documentName}
                onChange={(e) => setUploadData({ ...uploadData, documentName: e.target.value })}
              />

              <TextField
                fullWidth
                label="Document Number (Optional)"
                value={uploadData.documentNumber}
                onChange={(e) => setUploadData({ ...uploadData, documentNumber: e.target.value })}
              />

              <DatePicker
                label="Issue Date"
                value={uploadData.issueDate}
                onChange={(date) => setUploadData({ ...uploadData, issueDate: date })}
                slotProps={{ textField: { fullWidth: true } }}
              />

              <DatePicker
                label="Expiry Date"
                value={uploadData.expiryDate}
                onChange={(date) => setUploadData({ ...uploadData, expiryDate: date })}
                slotProps={{ textField: { fullWidth: true } }}
              />

              <TextField
                fullWidth
                multiline
                rows={2}
                label="Notes"
                value={uploadData.notes}
                onChange={(e) => setUploadData({ ...uploadData, notes: e.target.value })}
              />

              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUploadIcon />}
                fullWidth
              >
                {uploadFile ? uploadFile.name : 'Choose File'}
                <input
                  type="file"
                  hidden
                  onChange={handleFileChange}
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                />
              </Button>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setUploadDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleUpload} 
              variant="contained"
              disabled={!uploadFile || !uploadData.categoryId || !uploadData.documentName}
            >
              Upload
            </Button>
          </DialogActions>
        </Dialog>

        {/* Category Dialog */}
        <Dialog open={categoryDialog} onClose={() => setCategoryDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Create Document Category</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                fullWidth
                label="Category Name"
                value={newCategory.name}
                onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
              />

              <TextField
                fullWidth
                multiline
                rows={2}
                label="Description"
                value={newCategory.description}
                onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
              />

              <FormControl fullWidth>
                <InputLabel>Document Type</InputLabel>
                <Select
                  value={newCategory.documentType}
                  onChange={(e) => setNewCategory({ ...newCategory, documentType: e.target.value })}
                  label="Document Type"
                >
                  <MenuItem value="identification">Identification</MenuItem>
                  <MenuItem value="vehicle">Vehicle</MenuItem>
                  <MenuItem value="insurance">Insurance</MenuItem>
                  <MenuItem value="pet">Pet</MenuItem>
                  <MenuItem value="medical">Medical</MenuItem>
                  <MenuItem value="legal">Legal</MenuItem>
                  <MenuItem value="financial">Financial</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>

              <FormControlLabel
                control={
                  <Switch
                    checked={newCategory.requiresExpiry}
                    onChange={(e) => setNewCategory({ ...newCategory, requiresExpiry: e.target.checked })}
                  />
                }
                label="Requires Expiry Date"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={newCategory.requiresVerification}
                    onChange={(e) => setNewCategory({ ...newCategory, requiresVerification: e.target.checked })}
                  />
                }
                label="Requires Verification"
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCategoryDialog(false)}>Cancel</Button>
            <Button 
              onClick={createCategory} 
              variant="contained"
              disabled={!newCategory.name || !newCategory.documentType}
            >
              Create
            </Button>
          </DialogActions>
        </Dialog>

        {/* Document View Dialog */}
        <Dialog open={documentViewDialog} onClose={() => setDocumentViewDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {selectedDocument && getStatusIcon(selectedDocument.status)}
              Document Details
            </Box>
          </DialogTitle>
          <DialogContent>
            {selectedDocument && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Document Name</Typography>
                  <Typography variant="body1" gutterBottom>{selectedDocument.documentName}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Category</Typography>
                  <Typography variant="body1" gutterBottom>{selectedDocument.categoryId.name}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Document Number</Typography>
                  <Typography variant="body1" gutterBottom>{selectedDocument.documentNumber || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Status</Typography>
                  <Box sx={{ mt: 0.5 }}>{getStatusChip(selectedDocument.status)}</Box>
                </Grid>
                {selectedDocument.issueDate && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2">Issue Date</Typography>
                    <Typography variant="body1" gutterBottom>
                      {format(new Date(selectedDocument.issueDate), 'MM/dd/yyyy')}
                    </Typography>
                  </Grid>
                )}
                {selectedDocument.expiryDate && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2">Expiry Date</Typography>
                    <Typography variant="body1" gutterBottom>
                      {format(new Date(selectedDocument.expiryDate), 'MM/dd/yyyy')}
                    </Typography>
                  </Grid>
                )}
                {selectedDocument.notes && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">Notes</Typography>
                    <Typography variant="body1" gutterBottom>{selectedDocument.notes}</Typography>
                  </Grid>
                )}
                {selectedDocument.verifiedBy && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2">Verified By</Typography>
                    <Typography variant="body1" gutterBottom>{selectedDocument.verifiedBy.name}</Typography>
                  </Grid>
                )}
                {selectedDocument.verifiedAt && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2">Verified At</Typography>
                    <Typography variant="body1" gutterBottom>
                      {format(new Date(selectedDocument.verifiedAt), 'MM/dd/yyyy HH:mm')}
                    </Typography>
                  </Grid>
                )}
                {selectedDocument.rejectionReason && (
                  <Grid item xs={12}>
                    <Alert severity="error">
                      <Typography variant="subtitle2">Rejection Reason</Typography>
                      <Typography variant="body2">{selectedDocument.rejectionReason}</Typography>
                    </Alert>
                  </Grid>
                )}
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDocumentViewDialog(false)}>Close</Button>
            {selectedDocument && (
              <Button 
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={() => handleDownloadDocument(selectedDocument)}
              >
                Download
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Requirement Dialog */}
        <Dialog open={requirementDialog} onClose={() => setRequirementDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Create Document Requirement</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <FormControl fullWidth>
                <InputLabel>Location</InputLabel>
                <Select
                  value={newRequirement.locationId}
                  onChange={(e) => setNewRequirement({ ...newRequirement, locationId: e.target.value })}
                  label="Location"
                >
                  {locations.map((location) => (
                    <MenuItem key={location._id} value={location._id}>
                      {location.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Document Category</InputLabel>
                <Select
                  value={newRequirement.categoryId}
                  onChange={(e) => setNewRequirement({ ...newRequirement, categoryId: e.target.value })}
                  label="Document Category"
                >
                  {categories.map((cat) => (
                    <MenuItem key={cat._id} value={cat._id}>
                      {cat.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Requirement Type</InputLabel>
                <Select
                  value={newRequirement.requirementType}
                  onChange={(e) => setNewRequirement({ ...newRequirement, requirementType: e.target.value })}
                  label="Requirement Type"
                >
                  <MenuItem value="check_in">Check-in</MenuItem>
                  <MenuItem value="reservation">Reservation</MenuItem>
                  <MenuItem value="seasonal">Seasonal</MenuItem>
                  <MenuItem value="pet">Pet</MenuItem>
                  <MenuItem value="vehicle">Vehicle</MenuItem>
                  <MenuItem value="age_verification">Age Verification</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                type="number"
                label="Reminder Days"
                value={newRequirement.reminderDays}
                onChange={(e) => setNewRequirement({ ...newRequirement, reminderDays: parseInt(e.target.value) })}
              />

              <TextField
                fullWidth
                multiline
                rows={2}
                label="Message"
                value={newRequirement.message}
                onChange={(e) => setNewRequirement({ ...newRequirement, message: e.target.value })}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={newRequirement.isMandatory}
                    onChange={(e) => setNewRequirement({ ...newRequirement, isMandatory: e.target.checked })}
                  />
                }
                label="Mandatory"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={newRequirement.blockCheckIn}
                    onChange={(e) => setNewRequirement({ ...newRequirement, blockCheckIn: e.target.checked })}
                  />
                }
                label="Block Check-in if Missing"
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRequirementDialog(false)}>Cancel</Button>
            <Button 
              onClick={createRequirement} 
              variant="contained"
              disabled={!newRequirement.locationId || !newRequirement.categoryId || !newRequirement.requirementType}
            >
              Create
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default GuestDocumentsPage;